import createClient from 'openapi-fetch'
import type { components, paths } from './api.schema'
import { accessToken } from './supabase'

/** Types of the API, generated from its OpenAPI document. */
export type Schemas = components['schemas']

/** Stable error code of the API. */
export type ErrorCode = Schemas['ErrorCode']

/** An error answered by the API, or a request that never reached it (`status` 0). */
export class ApiError extends Error {
  /**
   * @param status - HTTP status, 0 when the request failed before any answer.
   * @param code - What went wrong; branch on this, never on the message.
   * @param body - The full error body, with `retry_after` or `ban` when relevant.
   */
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    readonly body?: Schemas['ErrorBody'],
  ) {
    super(body?.message ?? code)
    this.name = 'ApiError'
  }
}

/** Base URL of the Rust API. */
export const API_URL = import.meta.env.VITE_API_URL ?? 'https://api.t.armx.be'

/** Typed client of the Rust API. Sends the Supabase access token when signed in. */
export const api = createClient<paths>({ baseUrl: API_URL })

api.use({
  async onRequest({ request }) {
    const token = await accessToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    return request
  },
})

function isErrorBody(value: unknown): value is Schemas['ErrorBody'] {
  return typeof value === 'object' && value !== null && 'code' in value && 'message' in value
}

/**
 * Waits for an API call and returns its data.
 * @param call - A call made with {@link api}.
 * @returns The response body (`undefined` for 204).
 * @throws {ApiError} When the API answers with an error or cannot be reached.
 * @example
 * const me = await unwrap(api.GET('/me'))
 */
export async function unwrap<R extends { data?: unknown; error?: unknown; response: Response }>(
  call: Promise<R>,
): Promise<NonNullable<R['data']>> {
  let result: R
  try {
    result = await call
  } catch {
    throw new ApiError(0, 'unavailable')
  }
  if (result.error !== undefined || !result.response.ok) {
    const body = isErrorBody(result.error) ? result.error : undefined
    throw new ApiError(result.response.status, body?.code ?? 'internal', body)
  }
  return result.data as NonNullable<R['data']>
}
